import { z } from 'zod';
import { endpoint } from './common';

// Signing in and out, and creating an account. None of these need a signed-in caller.

// Browsers receive an HttpOnly session cookie, never a credential in the response.
// Native clients receive an opaque token to keep in platform secure storage and send as a Bearer header.
const session = z.object({ sessionToken: z.string().regex(/^[A-Za-z0-9_-]{43}$/).optional() });
const clientKind = z.enum(['web', 'native']).optional();

// The rule for a password, wherever one is chosen.
export const newPassword = z.string().min(6, 'Password must be at least 6 characters long').max(200);

export const login = endpoint({
    method: 'POST',
    path: '/login',
    body: z.object({
        email: z.string().trim().min(1, 'email is required'),
        password: z.string().min(1, 'password is required'),
        client: clientKind,
    }),
    response: session,
});

export const signup = endpoint({
    method: 'POST',
    path: '/signup',
    body: z.object({
        email: z.string().trim().email('email must be an email address'),
        password: newPassword,
    }),
    response: z.object({ email: z.string() }),
});

// A throwaway account for trying the app without signing up. It starts with a sample board (tasks for the next 30 days, a kiosk note
// and photos); `timeZone` (the browser's) is what the sample tasks' times of day are in. `boardId` is that board.
export const temporaryLogin = endpoint({
    method: 'POST',
    path: '/temporaryLogin',
    body: z.object({ timeZone: z.string().max(64).optional(), client: clientKind }).default({}),
    response: session.extend({ boardId: z.string().optional() }),
});

// The current session is checked on the server, including after a browser reload.
export const currentSession = endpoint({
    method: 'GET', path: '/auth/session',
    response: z.object({ authenticated: z.boolean() }),
});
export const logout = endpoint({
    method: 'POST', path: '/logout',
    body: z.object({}).default({}),
    response: z.unknown(),
});

// A forgotten password. The first asks for a link to be emailed to the address; the answer is the same whether or not an account has that
// address, so nobody can use it to find out who has one. The link (valid for an hour, good once) leads to a page that sends the second
// request with the new password. Signing in again is then needed: sign-ins from before the reset stop working.
export const requestPasswordReset = endpoint({
    method: 'POST',
    path: '/passwordReset/request',
    body: z.object({ email: z.string().trim().email('email must be an email address') }),
    response: z.object({ requested: z.literal(true) }),
});

export const confirmPasswordReset = endpoint({
    method: 'POST',
    path: '/passwordReset/confirm',
    body: z.object({ token: z.string().min(16).max(200), password: newPassword }),
    response: z.object({ reset: z.literal(true) }),
});

// --- signing in with a Google or Apple account ------------------------------------------------------------------------
//
// The browser asks to start, is sent to the provider, and comes back to the API (the provider's redirect, which is a browser
// navigation and so not described here), which sends it on to the web app with a short-lived code in the address. The web app trades that code
// for a session. Both ends of the trip are tied to the browser that began it: it makes a random `verifier`, keeps it, and gives the
// API only its hash (`challenge`, the base64url of the SHA-256 of the verifier). The code is useless without the verifier, so a code
// that is stolen, or one made by someone else's sign-in and handed to a victim, cannot be turned into a session.

export const ssoProvider = z.enum(['google', 'apple']);
export type SsoProvider = z.infer<typeof ssoProvider>;

// Which providers this server has set up, for the login page to show a button for each.
export const getSsoProviders = endpoint({
    method: 'GET',
    path: '/auth/sso/providers',
    response: z.object({ providers: z.array(ssoProvider) }),
});

const ssoVerifier = z.string().regex(/^[A-Za-z0-9_-]{43,128}$/, 'verifier must be 43 to 128 URL-safe characters');
export const ssoChallenge = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'challenge must be a base64url SHA-256 hash');

export const ssoStart = endpoint({
    method: 'POST',
    path: '/auth/sso/start',
    body: z.object({ provider: ssoProvider, challenge: ssoChallenge }),
    response: z.object({ url: z.string() }),            // where to send the browser: the provider's sign-in page
});

export const ssoExchange = endpoint({
    method: 'POST',
    path: '/auth/sso/exchange',
    body: z.object({ code: z.string().min(16).max(200), verifier: ssoVerifier, client: clientKind }),
    response: session,
});

// Why a sign-in that went to a provider and came back did not work, as the web app is told (in the address of the login page).
export const ssoErrors = ['cancelled', 'expired', 'failed', 'email_not_verified', 'limit', 'unavailable'] as const;
export type SsoError = typeof ssoErrors[number];
